/**
 * Created: 2026-08-12
 * Initial commit: Merge role switching and site context into unified role provider
 *
 * Unified context for role and site management. Replaces the separate
 * RoleSwitchProvider and SiteProvider with a single source of truth for
 * the user's active role (app admin or site admin) and current site.
 */

import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import type { SelectedRole } from '../LoginRedirector/RoleSelectionDialog'

export interface SiteInfo {
  id: string
  name: string
  roles: string[]
  avatar?: string | null
}

/** Flattens a `SiteMembership` from the API into a `SiteInfo`. */
export function toSiteInfo(membership: {
  site: { id: string; name: string; avatar?: string | null }
  roles: string[]
}): SiteInfo {
  const { site, roles } = membership
  return { id: site.id, name: site.name, avatar: site.avatar, roles }
}

interface RoleContextValue {
  sites: SiteInfo[]
  currentSite: SiteInfo | undefined
  isAppAdmin: boolean
  selectedRole: SelectedRole
  switchRole: () => void
  addSite: (site: SiteInfo) => void
}

const RoleContext = createContext<RoleContextValue | undefined>(undefined)

interface RoleProviderProps extends PropsWithChildren {
  sites: SiteInfo[]
  isAppAdmin: boolean
  selectedRole: SelectedRole
  onSwitchRole: () => void
}

export function RoleProvider({
  sites: initialSites,
  isAppAdmin,
  selectedRole,
  onSwitchRole,
  children,
}: RoleProviderProps) {
  const [sites, setSites] = useState<SiteInfo[]>(initialSites)

  const currentSite = useMemo(() => {
    if (selectedRole.type === 'app_admin') {
      return undefined
    }
    return sites.find((s) => s.id === selectedRole.site.id)
  }, [sites, selectedRole])

  const addSite = useCallback((site: SiteInfo) => {
    setSites((prev) => [...prev, site])
  }, [])

  const value = useMemo(
    () => ({
      sites,
      currentSite,
      isAppAdmin,
      selectedRole,
      switchRole: onSwitchRole,
      addSite,
    }),
    [sites, currentSite, isAppAdmin, selectedRole, onSwitchRole, addSite],
  )

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>
}

export function useRole(): RoleContextValue {
  const context = useContext(RoleContext)
  if (!context) {
    throw new Error('useRole must be used within a RoleProvider')
  }
  return context
}
